import type { User } from '@/features/participant';
import { DateTransform } from '@/shared/utilities/class-validator-extends';
import { Transform, Type } from 'class-transformer';
import dayjs, { type Dayjs } from 'dayjs';
import i18next from 'i18next';

export enum ConversationRole {
  MEMBER = 'MEMBER',
  MODERATOR = 'MODERATOR',
  ADMIN = 'ADMIN',
  OWNER = 'OWNER'
}

/** Role hierarchy order (higher = more permissions) */
export const ConversationRoleOrder: Record<ConversationRole, number> = {
  [ConversationRole.MEMBER]: 0,
  [ConversationRole.MODERATOR]: 1,
  [ConversationRole.ADMIN]: 2,
  [ConversationRole.OWNER]: 3
};

/** Get role order value, defaults to -1 for null/undefined */
const getRoleOrder = (role: ConversationRole | null | undefined): number => (role ? ConversationRoleOrder[role] : -1);

/** Compare two roles, returns positive if role1 > role2 */
export const compareRoles = (
  role1: ConversationRole | null | undefined,
  role2: ConversationRole | null | undefined
): number => getRoleOrder(role1) - getRoleOrder(role2);

/** Check if role meets minimum required role */
export const hasMinRole = (
  role: ConversationRole | null | undefined,
  minRole: ConversationRole | null | undefined
): boolean => getRoleOrder(role) >= getRoleOrder(minRole);

/** Check if role1 is higher than role2 */
export const isHigherRole = (
  role1: ConversationRole | null | undefined,
  role2: ConversationRole | null | undefined
): boolean => getRoleOrder(role1) > getRoleOrder(role2);

/** Check if role1 is higher or equal than role2 */
export const isHigherOrEqualRole = (
  role1: ConversationRole | null | undefined,
  role2: ConversationRole | null | undefined
): boolean => getRoleOrder(role1) >= getRoleOrder(role2);

/** Check if role1 is lower or equal to role2 */
export const isLowerOrEqualRole = (
  role1: ConversationRole | null | undefined,
  role2: ConversationRole | null | undefined
): boolean => getRoleOrder(role1) <= getRoleOrder(role2);

// Backward compatibility alias
export const MemberRole = ConversationRole;
export type MemberRole = ConversationRole;

export class Member {
  userId: string;

  role: ConversationRole = ConversationRole.MEMBER;

  status: string;

  @Transform(DateTransform.toClass, { toClassOnly: true })
  @Transform(DateTransform.toPlain, { toPlainOnly: true })
  @Type(() => String)
  joinedAt: Dayjs;

  @Transform(DateTransform.toClass, { toClassOnly: true })
  @Transform(DateTransform.toPlain, { toPlainOnly: true })
  @Type(() => String)
  updatedAt: Dayjs;

  lastSeenMessageId: string;

  @Transform(DateTransform.toClass, { toClassOnly: true })
  @Transform(DateTransform.toPlain, { toPlainOnly: true })
  @Type(() => String)
  lastSeenTimestamp: Dayjs;

  isOnline = false;

  user?: Nullable<User>;

  displayRole?: string;

  constructor(data: Partial<Member> = {}) {
    this.userId = data.userId || '';
    this.role = data.role || ConversationRole.MEMBER;
    this.status = data.status || '';
    this.joinedAt = data.joinedAt || dayjs();
    this.updatedAt = data.updatedAt || dayjs();
    this.lastSeenMessageId = data.lastSeenMessageId || '';
    this.lastSeenTimestamp = data.lastSeenTimestamp || dayjs();
    this.user = data.user;
    this.displayRole = this.getDisplayRole();
  }

  private getDisplayRole(): string {
    switch (this.role) {
      case ConversationRole.OWNER:
        return i18next.t('conversations.members.owner');
      case ConversationRole.ADMIN:
        return i18next.t('conversations.members.admin');
      case ConversationRole.MODERATOR:
        return i18next.t('conversations.members.moderator');
      default:
        return i18next.t('conversations.members.member');
    }
  }
}
